import React, { useMemo, useEffect, useState } from 'react';
import classNames from 'classnames';

import { ComponentForm } from '../../../ComponentForm/ComponentForm';

import { ComponentBody } from './ComponentBody/ComponentBody';

import type { ComponentResponse, ComponentInput } from '../../../../types';
import type { FC } from 'react';

import styles from './Component.module.scss';

/**
 * Temporary fix to replace problematic substructures with their ids.
 * `editor` may arrive from the API as a populated object ({ id, name, ... })
 * or already as a flat id string — either way the result carries a bare id.
 */
export function flattenComponentEditor(
  component: ComponentResponse
): ComponentResponse {
  const flat: Record<string, any> = { ...component };
  if (flat.editor?.id) {
    flat.editor = flat.editor.id;
  }

  return flat as ComponentResponse;
}

type Props = {
  component: ComponentResponse;
  index: number;
  onAddClick: (index: number, component: Partial<ComponentResponse>) => void;
  onRemoveClick: (index: number) => void;
  onFormSubmit: (
    componentDraft: Partial<
      ComponentInput & {
        id: string;
        pageId: string;
        index: number;
      }
    >
  ) => Promise<void>;
  onFormCancel: (index: number, component: Partial<ComponentResponse>) => void;
  onEditStart: () => void;
  onEditEnd: () => void;
  isEditable?: boolean;
};

export const Component: FC<Props> = ({
  component,
  index,
  onAddClick,
  onRemoveClick,
  onFormCancel,
  onFormSubmit,
  onEditStart,
  onEditEnd,
  isEditable = true,
}) => {
  const isNew = component.id === undefined;
  const [isEditing, setIsEditing] = useState(isNew); // expand the form by default when creating a new component

  useEffect(() => {
    if (isNew) {
      onEditStart();
    }
  }, [isNew, onEditStart]);

  function onFormCancelClick() {
    setIsEditing(false);

    onFormCancel(index, component);
    onEditEnd();
  }

  async function onSubmit(
    componentDraft: Partial<
      ComponentInput & {
        id: string;
        pageId: string;
        index: number;
      }
    >
  ) {
    await onFormSubmit(componentDraft);

    setIsEditing(false);
    onEditEnd();
  }

  function onEditClick() {
    if (isEditable) {
      setIsEditing(!isEditing);

      onEditStart();
    }
  }

  // Temporary fix to replace problematic substructures with their ids
  const flatComponent = useMemo(
    () => flattenComponentEditor(component),
    [component]
  );

  return (
    <div
      className={classNames(styles.component, {
        [styles.isEditing]: isEditing,
        [styles.isEditable]: isEditable,
      })}
    >
      {!isEditing ? (
        <ComponentBody
          component={component}
          index={index}
          onAddClick={onAddClick}
          onEditClick={onEditClick}
          onRemoveClick={onRemoveClick}
          isEditable={isEditable}
        />
      ) : (
        <ComponentForm
          component={flatComponent}
          onCancelClick={onFormCancelClick}
          onSubmit={onSubmit}
        />
      )}
    </div>
  );
};
